import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/twm";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none  disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90 text-white text-base",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				blackBtn:
					"bg-[#1D1D1B] hover:bg-black w-full h-[3rem] sm:h-[3.5rem] lg:h-[4rem] p-[0.625rem] rounded-full hover:shadow-md text-center text-white text-[0.825rem] sm:text-sm lg:text-base font-medium leading-4 sm:leading-5 lg:leading-[1.575rem]",
				outline:
					"w-full bg-transparent hover:bg-[#FE4D010D] h-[2.45rem] sm:h-[2.6rem] md:h-[2.75rem] lg:h-[2.875rem] p-[0.9375rem_2.125rem] border border-[#FE4D01] rounded-xl hover:shadow-sm text-[#FE4D01] text-[0.825rem] sm:text-sm lg:text-base font-medium leading-5 sm:leading-6",
				primary:
					"bg-[#FE4D01] hover:bg-[#E44501] w-full h-[2.35rem] sm:h-10 md:h-[2.75rem] p-[0.625rem] rounded-xl hover:shadow-sm text-center text-white text-[0.825rem] sm:text-sm lg:text-base font-medium leading-5 sm:leading-6 transition-colors",
				secondary:
					"bg-yellow-500 text-dark hover:bg-yellow-400/90 text-xs leading-loose",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			// size: 'default',
		},
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";

		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);

Button.displayName = "Button";

export { Button, buttonVariants };
